import React, { useState } from 'react';
import { MenuIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../utils/translations';
export function Header() {
  const {
    language,
    toggleLanguage
  } = useLanguage();
  const t = translations[language];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };
  return <header className="fixed top-0 left-0 right-0 z-50 bg-transparent" data-id="element-74">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-75">
        <div className="flex justify-between items-center h-20" data-id="element-76">
          {/* Logo */}
          <button onClick={() => scrollToSection('home')} className="flex items-center transition-transform duration-200 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-white/50 rounded" aria-label="Go to home" data-id="element-77">
            <img src="https://cdn.magicpatterns.com/uploads/5rM3zo3htVqrwWX2CQZ1op/logo.png" alt="Crossroads Consulting LDA" className="h-12 w-auto" onError={e => {
            e.currentTarget.style.display = 'none';
            const textFallback = e.currentTarget.nextElementSibling as HTMLElement;
            if (textFallback) textFallback.style.display = 'block';
          }} data-id="element-78" />
            <span className="hidden ml-2 text-xl font-bold text-white" data-id="element-79">
              CROSSROADS CONSULTING LDA
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8" role="navigation" data-id="element-80">
            <button onClick={() => scrollToSection('home')} className="relative text-white font-medium transition-colors duration-200 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1 group" data-id="element-81">
              {t.nav.home}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-300 transition-all duration-300 group-hover:w-full" data-id="element-82"></span>
            </button>
            <button onClick={() => scrollToSection('about')} className="relative text-white font-medium transition-colors duration-200 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1 group" data-id="element-83">
              {t.nav.about}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-300 transition-all duration-300 group-hover:w-full" data-id="element-84"></span>
            </button>
            <button onClick={() => scrollToSection('services')} className="relative text-white font-medium transition-colors duration-200 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1 group" data-id="element-85">
              {t.nav.services}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-300 transition-all duration-300 group-hover:w-full" data-id="element-86"></span>
            </button>
            <button onClick={() => scrollToSection('contact')} className="relative text-white font-medium transition-colors duration-200 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1 group" data-id="element-87">
              {t.nav.contact}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-300 transition-all duration-300 group-hover:w-full" data-id="element-88"></span>
            </button>

            {/* Language Toggle - Desktop */}
            <button onClick={toggleLanguage} className="flex items-center text-white font-medium transition-colors duration-200 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1 ml-4 border border-white/30 bg-white/10 backdrop-blur-sm" aria-label="Toggle language" data-id="element-89">
              <span className={language === 'en' ? 'font-bold' : 'opacity-70'} data-id="element-90">
                EN
              </span>
              <span className="mx-1 opacity-50" data-id="element-91">|</span>
              <span className={language === 'pt' ? 'font-bold' : 'opacity-70'} data-id="element-92">
                PT
              </span>
            </button>
          </nav>

          {/* Mobile: Language Toggle + Hamburger */}
          <div className="flex md:hidden items-center gap-3" data-id="element-93">
            {/* Language Toggle - Mobile (always visible) */}
            <button onClick={toggleLanguage} className="flex items-center text-white font-medium text-sm transition-colors duration-200 hover:text-blue-300 focus:outline-none focus:ring-2 focus:ring-white/50 rounded px-2 py-1 border border-white/30 bg-white/10 backdrop-blur-sm" aria-label="Toggle language" data-id="element-94">
              <span className={language === 'en' ? 'font-bold' : 'opacity-70'} data-id="element-95">
                EN
              </span>
              <span className="mx-1 opacity-50" data-id="element-96">|</span>
              <span className={language === 'pt' ? 'font-bold' : 'opacity-70'} data-id="element-97">
                PT
              </span>
            </button>

            {/* Hamburger Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-white p-2 focus:outline-none focus:ring-2 focus:ring-white/50 rounded" aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen} data-id="element-98">
              {mobileMenuOpen ? <XIcon className="w-6 h-6" data-id="element-99" /> : <MenuIcon className="w-6 h-6" data-id="element-100" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && <nav className="md:hidden bg-black/80 backdrop-blur-md rounded-lg mt-1 py-4 px-4 space-y-1" role="navigation" data-id="element-101">
            <button onClick={() => scrollToSection('home')} className="block w-full text-left text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 hover:bg-white/10" data-id="element-102">
              {t.nav.home}
            </button>
            <button onClick={() => scrollToSection('about')} className="block w-full text-left text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 hover:bg-white/10" data-id="element-103">
              {t.nav.about}
            </button>
            <button onClick={() => scrollToSection('services')} className="block w-full text-left text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 hover:bg-white/10" data-id="element-104">
              {t.nav.services}
            </button>
            <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 hover:bg-white/10" data-id="element-105">
              {t.nav.contact}
            </button>
          </nav>}
      </div>
    </header>;
}